<template>
  <div>
    <scrollable-list
      ref="slider"
      :items="cardItems"
      @init="initNavigationButtons"
      @slideChange="handleSlideChange"
      @transitionStart="handleTransitionStart"
      @updated="initNavigationButtons"
    />
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import $ from 'jquery';

import ScrollableList from './ScrollableList';
import NewBookingModal from '../Modal/BookingForm';
import BookingDetailsModal from '../Modal/BookingDetails';
import BookingMixins from '../../../mixins/booking-mixins';
import ModalMixins from '../../../mixins/modal-mixins';
import { setInterval } from 'timers';

export default {
  created() {
    // Sets BrightSign Hardware LED color based on booking status
    const pollStatus = () => {
      // Get all bookings for current hour and filter out bookings that end this hour
      const bookingsThisHour = this.getBookingsByHour(this.bookings, moment().get('hour'))
        .filter(b => moment(b.end_date).format('hh:mm') !== `${moment().get('hour')}:00`);

      // Set LED to green and break if no bookings picked
      if (!bookingsThisHour.length) {
        this.setBSHWLED('green');
        return;
      }

      // Check if any bookings at current time
      const bookingsNow = bookingsThisHour
        .filter((b) => {
          const timeNow = moment();
          const bookingStart = moment(b.start_date);
          const bookingEnd = moment(b.end_date);
          return timeNow.isBetween(bookingStart, bookingEnd);
        });

      // Set LED to green and break if no booking at current time
      if (!bookingsNow.length) {
        this.setBSHWLED('green');
        return;
      }

      // Filter bookings with invitees and not checked in yet
      const checkins = bookingsThisHour
        .filter(b => b.invites_list.length && !b.checkedin);

      // Check if current time falls between any event checkins
      if (checkins.length) {
        // Checkin time intersect flag
        let isCheckInPeriod = false;
        // Compute checkin time for each checkin event (15 mins <= event >= 15 mins)
        for (const checkin of checkins) {
          const timeNow = moment();
          const checkinStart = moment(checkin.start_date).subtract(15, 'minutes');
          const checkinEnd = moment(checkin.start_date).add(15, 'minutes');

          // Set led to yellow if within checkin time range
          if (timeNow.isBetween(checkinStart, checkinEnd)) {
            isCheckInPeriod = true;
          }
        }
        // Set led based on current time intersection
        isCheckInPeriod ? this.setBSHWLED('yellow') : this.setBSHWLED('red');
      } else { // Set meeting room status to booked if no checkins needed
        this.setBSHWLED('red');
      }
    };
    pollStatus();
    // Poll application booking state on a 30 second interval
    setInterval(pollStatus, 30 * 1000);
  },
  components: { ScrollableList },

  mixins: [BookingMixins, ModalMixins],

  data: () => ({
    modalActiveClass: '',
    isSliding: false,
    registeredEventIndeces: [],
  }),

  props: {
    bookings: Array,
  },

  computed: {
    ...mapState(['cards', 'space', 'device', 'selectedDate']),
    ...mapGetters([
      'spaceEndDate',
      'spaceStartDate',
      'canAddForSelectedDate',
      'isSelectedDateToday',
    ]),
    goTo() {
      return this.cards.goTo;
    },
    isDeviceSpace() {
      return this.space.id === this.device.space_id;
    },
    cardItems() {
      const cardItems = [];
      let nextBooking = null;

      let earliestAvailableTime = this.spaceStartDate;
      if (this.isSelectedDateToday) {
        if (moment() > this.spaceStartDate) {
          earliestAvailableTime = this.getWithIncrementMinutes(moment());
        } else {
          earliestAvailableTime = this.getWithIncrementMinutes(
            this.spaceStartDate,
          );
        }
      }

      if (this.bookings.length > 0) {
        if (
          this.canAddForSelectedDate &&
          earliestAvailableTime <
            this.getAdjustedDate(this.bookings[0].start_date)
        ) {
          cardItems.push({
            id: 'new-first',
            type: 'NewBooking',
            props: {
              startDate: earliestAvailableTime,
              endDate: this.getAdjustedDate(this.bookings[0].start_date),
            },
            onclick: this.handleNewBookingClick,
            startHour: earliestAvailableTime.hour(),
            endHour: this.getAdjustedDate(this.bookings[0].start_date).hour(),
          });
        }

        this.bookings.forEach((booking, index) => {
          cardItems.push({
            type: 'Card',
            id: booking.id,
            props: { booking },
            onclick: this.handleBookingClick,
            startHour: moment(booking.start_date).hour(),
            endHour: moment(booking.end_date).hour(),
          });

          // Check if next slot is empty
          if (index < this.bookings.length) {
            nextBooking = this.bookings[index + 1];

            const endDate = this.getAdjustedDate(booking.end_date);
            let nextBookingStart = endDate;

            if (this.isSelectedDateToday) {
              nextBookingStart = this.getWithIncrementMinutes(endDate, false);
            }

            if (
              nextBooking &&
              this.canAddForSelectedDate &&
              this.getAdjustedDate(nextBooking.start_date) !== endDate &&
              this.getTimeDifferenceDuration(
                // eslint-disable-next-line
                nextBookingStart,
                this.getAdjustedDate(nextBooking.start_date),
              ).asMinutes() >= 30
            ) {
              let availableCardEndDate = nextBooking.start_date;
              if (
                this.getAdjustedDate(nextBooking.start_date) > this.spaceEndDate
              ) {
                availableCardEndDate = this.spaceEndDate;
              }

              cardItems.push({
                id: `new-${index}`,
                type: 'NewBooking',
                props: {
                  startDate: nextBookingStart,
                  endDate: availableCardEndDate,
                },
                onclick: this.handleNewBookingClick,
                startHour: nextBookingStart.hour(),
                endHour: moment(nextBooking.start_date).hour(),
              });
            }
          }
        });

        const lastBooking = this.bookings[this.bookings.length - 1];
        const lastCardStart = this.getWithIncrementMinutes(
          this.getAdjustedDate(lastBooking.end_date),
          false,
        );

        if (
          this.canAddForSelectedDate &&
          lastCardStart < this.spaceEndDate &&
          this.getTimeDifferenceDuration(
            lastCardStart,
            this.spaceEndDate,
          ).asMinutes() >= 30
        ) {
          cardItems.push({
            id: 'new-last',
            type: 'NewBooking',
            props: {
              startDate: lastCardStart,
              endDate: this.spaceEndDate,
            },
            onclick: this.handleNewBookingClick,
            startHour: lastCardStart.hour(),
            endHour: this.spaceEndDate.hour(),
          });
        }
      } else if (this.canAddForSelectedDate) {
        cardItems.push({
          id: 'new',
          type: 'NewBooking',
          props: {
            startDate: earliestAvailableTime,
            endDate: this.spaceEndDate,
          },
          onclick: this.handleNewBookingClick,
          startHour: earliestAvailableTime.hour(),
          endHour: this.spaceEndDate.hour(),
        });
      }

      return cardItems;
    },
  },

  methods: {
    ...mapActions(['setPin']),

    getWithIncrementMinutes(startDate, reverse = true) {
      const date = moment(startDate);
      const minutes = 30;

      if (!reverse) {
        while (date.diff(moment(), 'minutes') < 0) {
          date.add(minutes, 'minutes');
        }
      } else {
        while (date.diff(moment(), 'minutes') > 30) {
          date.subtract(minutes, 'minutes');
        }
      }

      return date;
    },

    getTimeDifferenceDuration(startTime, endTime) {
      return moment.duration(moment(endTime).diff(moment(startTime)));
    },

    handleNewBookingClick(card, item) {
      if (this.isSliding) return;

      const from = moment
        .utc()
        .set({ hour: this.goTo, minute: 0 })
        .format('HH:mm');
      const to = moment
        .utc()
        .set({ hour: this.goTo, minute: 30 })
        .format('HH:mm');

      const bookingIndex = this.getCardIndex(item);

      if (this.$refs.slider.activeSlideIndex === bookingIndex) {
        this.showModal(
          NewBookingModal,
          {
            time: [from, to],
            startDate: item.props.startDate,
            endDate: item.props.endDate,
          },
          {},
          {
            'before-close': this.handleBeforeModalClose,
          },
        );
      }
    },

    getCardIndex(booking) {
      return this.cardItems.findIndex(({ id }) => id === booking.id);
    },

    handleBookingClick({ booking }) {
      if (this.isSliding) return;

      const bookingIndex = this.getCardIndex(booking);

      if (this.$refs.slider.activeSlideIndex === bookingIndex) {
        this.showModal(
          BookingDetailsModal,
          { booking },
          {},
          {
            'before-close': this.handleBeforeModalClose,
          },
        );
      }
    },

    handleBeforeModalClose() {
      this.setPin(null);
    },

    handleTransitionStart() {
      this.initNavigationButtons();
    },

    handleSlideChange() {
      this.$nextTick(() => {
        this.initNavigationButtons();
      });
    },

    initNavigationButtons() {
      setTimeout(() => {
        $('.swiper-slide.swiper-slide-active').off('click');
        $('.swiper-slide.swiper-slide-next')
          .off('click')
          .on('click', () => {
            this.$refs.slider.next();
          });
        $('.swiper-slide.swiper-slide-prev')
          .off('click')
          .on('click', () => {
            this.$refs.slider.prev();
          });
      }, 200);
    },
  },

  watch: {
    goTo(hour) {
      const potentialCards = this.cardItems.reduce(
        (p, { startHour, endHour }, currIndex) => {
          if (startHour <= hour && hour <= endHour) {
            p.push({
              index: currIndex,
              startHour,
              endHour,
            });
          }

          return p;
        },
        [],
      );

      const card = potentialCards.find(({ startHour }) => startHour === hour);
      if (card) {
        this.$refs.slider.goTo(card.index);
      } else if (potentialCards.length > 0) {
        this.$refs.slider.goTo(potentialCards[0].index);
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Booking/Card/List.vue